export function normalizeDeg360(deg: number): number {
  const n = deg % 360;
  return n < 0 ? n + 360 : n;
}

export function normalizeDeg180(deg: number): number {
  let n = normalizeDeg360(deg);
  if (n > 180) n -= 360;
  return n;
}

/**
 * Vector-frame relative wind angle in shot-aligned frame.
 *
 * Inputs:
 * - windToDeg: direction wind is traveling toward
 * - shotAzimuthDeg: direction shot is traveling toward
 *
 * Output convention:
 * -   0 = straight helping
 * - +90 = pushing toward golfer's RIGHT
 * - -90 = pushing toward golfer's LEFT
 * - ±180 = straight hurting
 *
 * This is best for drawing arrows / vector visualization.
 */
export function relativeWindDeg(
  windToDeg: number,
  shotAzimuthDeg: number
): number {
  return normalizeDeg180(windToDeg - shotAzimuthDeg);
}

/**
 * Vector-frame bucket labels.
 *
 * Interprets the wind by where it is traveling relative to the shot.
 * Best matched to the compass arrow / vector display.
 */
export function classifyRelativeWind(relativeDeg: number): string {
  const a = normalizeDeg180(relativeDeg);
  const absA = Math.abs(a);

  if (absA <= 22.5) return "helping";
  if (absA >= 157.5) return "hurting";

  if (a > 22.5 && a < 67.5) return "right-quarter-helping";
  if (a >= 67.5 && a <= 112.5) return "right-cross";
  if (a > 112.5 && a < 157.5) return "right-quarter-hurting";

  if (a < -22.5 && a > -67.5) return "left-quarter-helping";
  if (a <= -67.5 && a >= -112.5) return "left-cross";
  if (a < -112.5 && a > -157.5) return "left-quarter-hurting";

  return "helping";
}

/**
 * Body-frame relative wind angle in shot-aligned frame.
 *
 * Inputs:
 * - windFromDeg: meteorological wind direction (where wind is coming FROM)
 * - shotAzimuthDeg: direction shot is traveling toward
 *
 * Output convention:
 * -   0 = straight hurting (wind in face)
 * - +90 = striking right shoulder (right cross)
 * - -90 = striking left shoulder (left cross)
 * - ±180 = straight helping (wind at back)
 *
 * This matches golfer feel / shoulder-based interpretation.
 */
export function relativeWindFromDeg(
  windFromDeg: number,
  shotAzimuthDeg: number
): number {
  return normalizeDeg180(windFromDeg - shotAzimuthDeg);
}

/**
 * Body-frame bucket labels.
 *
 * Facing the target:
 * - front-right shoulder = right-quarter-hurting
 * - front-left shoulder  = left-quarter-hurting
 * - back-right shoulder  = right-quarter-helping
 * - back-left shoulder   = left-quarter-helping
 * - right side           = right-cross
 * - left side            = left-cross
 * - straight ahead       = hurting
 * - straight behind      = helping
 */
export function classifyWindBodyFrame(relativeFromDeg: number): string {
  const a = normalizeDeg180(relativeFromDeg);
  const absA = Math.abs(a);

  if (absA <= 22.5) return "hurting";
  if (absA >= 157.5) return "helping";

  if (a > 22.5 && a < 67.5) return "right-quarter-hurting";
  if (a >= 67.5 && a <= 112.5) return "right-cross";
  if (a > 112.5 && a < 157.5) return "right-quarter-helping";

  if (a < -22.5 && a > -67.5) return "left-quarter-hurting";
  if (a <= -67.5 && a >= -112.5) return "left-cross";
  if (a < -112.5 && a > -157.5) return "left-quarter-helping";

  return "hurting";
}